import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { FAQItem } from 'shared/api/FaqModel'
import { getCleanHTML } from 'shared/lib/getCleanHTML'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Button } from 'shared/ui/button'
import { Skeleton } from 'shared/ui/skeleton'

import styles from './styles.module.scss'

export type FaqStatus = 'loading' | 'success' | 'error'

interface Props {
  faqList: FAQItem[]
  status: FaqStatus
  onRetry: () => void
}

export const FaqAccordion = ({ faqList, status, onRetry }: Props) => {
  const { t } = useTranslation()
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)

  if (status === 'success' && faqList.length === 0) return null

  return (
    <section className={styles.container}>
      <h2 className={clsx('bold-18', styles.title)}>
        {t('support.faq_title')}
      </h2>

      {status === 'error' ? (
        <div className={styles.error}>
          <p className="medium-14">{t('support.faq_load_error')}</p>
          <Button
            type="button"
            borderRadius="sm"
            className={styles.retryButton}
            onClick={onRetry}
          >
            <span className="bold-16">{t('support.retry')}</span>
          </Button>
        </div>
      ) : (
        <div className={styles.list}>
          {status === 'loading'
            ? Array.from({ length: 4 }, (_, index) => (
                <div
                  className={styles.skeleton}
                  key={index}
                >
                  <Skeleton height={20} />
                </div>
              ))
            : faqList.map((item, index) => {
                const isExpanded = expandedSlug === item.slug
                const answerId = `faq-answer-${index}`

                return (
                  <article
                    className={styles.item}
                    key={item.slug}
                  >
                    <button
                      type="button"
                      className={styles.question}
                      aria-controls={answerId}
                      aria-expanded={isExpanded}
                      onClick={() =>
                        setExpandedSlug((currentSlug) =>
                          currentSlug === item.slug ? null : item.slug,
                        )
                      }
                    >
                      <span className="medium-16">{item.question}</span>
                      <AppIcon
                        id={AllIcons.ARROW_BOTTOM}
                        width={24}
                        height={24}
                        color="var(--grey-soft)"
                        className={clsx(
                          styles.chevron,
                          isExpanded && styles.chevronExpanded,
                        )}
                      />
                    </button>

                    {isExpanded && (
                      <div
                        id={answerId}
                        className={clsx('semi-bold-14', styles.answer)}
                        dangerouslySetInnerHTML={{
                          __html: getCleanHTML(item.answer || ''),
                        }}
                      />
                    )}
                  </article>
                )
              })}
        </div>
      )}
    </section>
  )
}
